

import React, { useMemo, useState, useEffect } from "react";
import { ReviewModal } from "components/ReviewModal";
import ConnectVendorModal from "components/ConnectVendorModal";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { sendTriggerEmails } from "utils/emailService";
import {
  Star,
  BadgeCheck,
  ShieldCheck,
  Building2,
  Search,
  CheckCircle2,
  Globe,
  Phone,
  Mail,
  MapPin,
  Check,
  Eye,
  ShoppingBag,
  Funnel,
  DollarSign,
  Award,
  MessageSquare,
  Plus,
  Users,
  ArrowUpRight,
  Tag,
  UserPlus2, Target, Layers, TrendingUp
} from "lucide-react";
import { VendorProfile } from "types";
import VendorDetailsSlider from "components/VendorDetailsSlider";
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "../../assets/css/Cartitems.css"; // for coupon badge styles
import { createPortal } from "react-dom";
import { getAuthCookie } from "../../utils/authCookies";

/** List (datatable) item - matches API marketplace-profiles/datatable */
type VendorListItem = {
  is_private?: boolean;
  _id: string;
  user_id: string;
  vendor_name: string;
  tag_line?: string;
  description?: string;
  category: string;
  verified?: boolean;
  partner?: boolean;
  logo?: string;
  core_specialties?: string[];
  Service?: string;
};

type DatatableResp = {
  data: VendorListItem[];
  limit: number;
  page: number;
  total: number;
  totalPages: number;
};

type VendorsPageProps = {
  user: "agent" | "realtor"; // we already pass this in your Agent/Realtor pages
};

const chip = (text: React.ReactNode, cls = "") => (
  <span className={`badge rounded-pill bg-light text-dark border me-2 mb-2 ${cls}`}>{text}</span>
);

const VendorsPage: React.FC<VendorsPageProps> = ({ user }) => {
  // list controls
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [searchInput, setSearchInput] = useState(""); // Input field value
  const [searchQuery, setSearchQuery] = useState(""); // Actual query for API
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [category, setCategory] = useState<string>("All");
  const [onlyPartners, setOnlyPartners] = useState(false);

  // list data
  const [rows, setRows] = useState<VendorListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // details modal data
  const [showDetails, setShowDetails] = useState(false);
  const [activeListItem, setActiveListItem] = useState<VendorListItem | null>(null);
  const [activeProfile, setActiveProfile] = useState<VendorProfile | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // review modal
  const [showReview, setShowReview] = useState(false);

  // reviews
  const [reviews, setReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);

  // profile ratings from reviews API (profileId -> { avg, count }) for cards
  const [profileRatings, setProfileRatings] = useState<Record<string, { avg: number; count: number }>>({});

  // linked suppliers (for Start/End Partnership)
  const [linkedSupplierIds, setLinkedSupplierIds] = useState<string[]>([]);
  const [loadingLinked, setLoadingLinked] = useState(false);
  // request vendor modal
  const [showRequestModal, setShowRequestModal] = useState(false);
  // connect vendor modal (request connection with vendor)
  const [showConnectModal, setShowConnectModal] = useState(false);
  
  // public discounts state - stores array of discount objects per user_id
  type Discount = {
    _id: string;
    name: string;
    code: string;
    type: "PERCENTAGE" | "FIXED_AMOUNT";
    value: number;
    status: boolean;
    start_date: string;
    end_date: string;
    [key: string]: any;
  };
  const [publicDiscounts, setPublicDiscounts] = useState<Record<string, Discount[]>>({});

  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const API_URL = process.env.REACT_APP_API_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const isAgent = location.pathname.includes('/agent');
  const baseRoute = isTeammember ? '/teammember' : isAgent ? '/agent' : '/realtor';
  // after rows state is set up
  const logoUrlMap = usePresignedMap(rows, "logo", token);

  // categories derived from current page
  const categories = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.category && set.add(r.category));
    return ["All", ...Array.from(set).sort()];
  }, [rows]);

  // client-side filters
  const filteredRows = rows.filter((r) => {
    if (r.is_private === true) return false;
    const byVerified = !verifiedOnly || !!r.verified;
    const byCategory = category === "All" || r.category === category;
    const byPartners = !onlyPartners || !!r.partner;
    const q = searchQuery.trim().toLowerCase();
    const byQuery =
      q.length === 0 ||
      r.vendor_name.toLowerCase().includes(q) ||
      (r.tag_line || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q);
    return byVerified && byCategory && byQuery && byPartners;
  });

  // separate "featured" by partner flag (as your existing UI)
  const featured = filteredRows.filter((v) => v.partner);
  const rest = filteredRows.filter((v) => !v.partner);
  console.log("filteredRows", rest);
  /** Load vendors list (datatable) */
  useEffect(() => {
    if (!API_URL) {
      setListError("Missing REACT_APP_API_URL");
      return;
    }
    if (!token) {
      setListError("Missing auth token");
      return;
    }

    const controller = new AbortController();
    (async () => {
      try {
        setLoadingList(true);
        setListError(null);

        const body = {
          page,
          limit,
          search: searchQuery, // matches your cURL
          filters: { status: "" },
          sort: { field: "created_at", order: "desc" },
          fields: ["verified", "vendor_name", "tag_line", "description", "category", "_id", "partner", "user_id", "logo", "core_specialties", "Service", "is_private"],
        };

        const resp = await fetch(`${API_URL}/marketplace-profiles/datatable`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        const json = (await resp.json()) as DatatableResp | { error?: string };
        if (!resp.ok) throw new Error((json as any)?.error || "Failed to fetch vendors");

        const dt = json as DatatableResp;
        setRows(Array.isArray(dt.data) ? dt.data : []);
        setTotalPages(dt.totalPages || 1);
      } catch (err) {
        if ((err as any).name === "AbortError") return;
        setListError(err instanceof Error ? err.message : "Failed to load vendors");
      } finally {
        setLoadingList(false);
      }
    })();

    return () => controller.abort();
  }, [API_URL, token, page, limit, searchQuery]);

  /** Load linked suppliers (decides Start vs End Partnership):
   *  agent -> GET /agent/suppliers/:userId
   *  realtor -> GET /brokerage-office/suppliers
   *  We only need supplier_id array to check linkage.
   */
  useEffect(() => {
    if (!API_URL || !token) return;
    const controller = new AbortController();

    (async () => {
      try {
        setLoadingLinked(true);
        const url =
          user === "realtor"
            ? `${API_URL}/brokerage-office/suppliers`
            : `${API_URL}/agent/suppliers/${userId}`;

        const resp = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        const data = await resp.json();
        if (!resp.ok) throw new Error((data as any)?.error || "Failed to fetch linked suppliers");

        // data: [{ supplier_id, name, email }, ...]
        const ids: string[] = Array.isArray(data) ? data.map((d: any) => d?.supplier_id).filter(Boolean) : [];
        setLinkedSupplierIds(ids);
      } catch (e) {
        // non-blocking for the page; user will still see "Start Partnership" by default
      } finally {
        setLoadingLinked(false);
      }
    })();

    return () => controller.abort();
  }, [API_URL, token, user, userId]);

  /** Open details + load full profile + reviews */
  const openDetails = async (item: VendorListItem) => {
    setActiveListItem(item);
    setActiveProfile(null);
    setDetailsError(null);
    setShowDetails(true);

    if (!API_URL || !token) {
      setDetailsError(!API_URL ? "Missing REACT_APP_API_URL" : "Missing auth token");
      return;
    }
    try {
      setLoadingDetails(true);
      const resp = await fetch(`${API_URL}/marketplace-profiles/${item._id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const json = (await resp.json()) as VendorProfile | { error?: string };
      if (!resp.ok) throw new Error((json as any)?.error || "Failed to load vendor details");
      setActiveProfile(json as VendorProfile);

      // Load reviews (optional)
      await loadVendorReviews(item._id);
    } catch (err) {
      setDetailsError(err instanceof Error ? err.message : "Failed to load vendor details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadVendorReviews = async (profileId: string) => {
    if (!API_URL || !token) return;
    try {
      setLoadingReviews(true);
      setReviewsError(null);

      const resp = await fetch(`${API_URL}/marketplace-reviews/profile/${profileId}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error((json as any)?.error || "Failed to load reviews");
      setReviews(Array.isArray(json) ? json : []);
    } catch (err) {
      setReviewsError(err instanceof Error ? err.message : "Failed to load reviews");
    } finally {
      setLoadingReviews(false);
    }
  };

  /** Fetch review aggregates for visible vendors (rating on cards) */
  useEffect(() => {
    if (!API_URL || !token || rows.length === 0) return;
    const controller = new AbortController();
    (async () => {
      const results = await Promise.all(
        rows.map(async (v) => {
          try {
            const resp = await fetch(`${API_URL}/marketplace-reviews/profile/${v._id}`, {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            });
            const list = await resp.json();
            const arr = Array.isArray(list) ? list : [];
            if (arr.length === 0) return { id: v._id, avg: 0, count: 0 };
            const sum = arr.reduce((a: number, r: any) => a + (Number(r.rating) || 0), 0);
            return { id: v._id, avg: sum / arr.length, count: arr.length };
          } catch {
            return { id: v._id, avg: 0, count: 0 };
          }
        })
      );
      const next: Record<string, { avg: number; count: number }> = {};
      results.forEach((r) => { next[r.id] = { avg: r.avg, count: r.count }; });
      setProfileRatings((prev) => ({ ...prev, ...next }));
    })();
    return () => controller.abort();
  }, [API_URL, token, rows]);

  const closeDetails = () => setShowDetails(false);
  const openReview = () => setShowReview(true);
  const closeReview = () => setShowReview(false);

  useEffect(() => {
    if (showDetails) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [showDetails]);

  useEffect(() => {
    if (!showDetails) return;
    const onEscape = (e: KeyboardEvent) => { if (e.key === "Escape") closeDetails(); };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [showDetails]);

  /** Fetch public discounts for a vendor */
const fetchPublicDiscounts = async (supplierIds: string[]) => {
  if (!API_URL || !token || !supplierIds.length) return;

  try {
    const resp = await fetch(`${API_URL}/discounts/public-codes/by-users`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        user_ids: supplierIds,
      }),
    });

    if (!resp.ok) return;
    const data = await resp.json();

    if (!Array.isArray(data?.results)) return;

    // Convert API response → store in state
    const mapped: Record<string, Discount[]> = {};
    const now = new Date();

    data.results.forEach((item: any) => {
      if (!item.user_id || !Array.isArray(item.discounts)) return;

      // Filter for active discounts: status=true, within date range
      const activeDiscounts = item.discounts.filter((discount: Discount) => {
        if (!discount.status) return false;
        
        const startDate = new Date(discount.start_date);
        const endDate = new Date(discount.end_date);
        
        return now >= startDate && now <= endDate;
      });

      if (activeDiscounts.length > 0) {
        mapped[item.user_id] = activeDiscounts;
      }
    });

    setPublicDiscounts((prev) => ({
      ...prev,
      ...mapped,
    }));

  } catch (err) {
    console.error("Failed to fetch public discount codes:", err);
  }
};


  /** Load public discounts for all visible vendors */
useEffect(() => {
const uniqueIds = Array.from(
  new Set(filteredRows.map((v: any) => v.user_id).filter(Boolean))
);


  // Only load vendors where data not already loaded
  const missingIds = uniqueIds.filter(
    (id) => !publicDiscounts[id]
  );

  if (missingIds.length > 0) {
    fetchPublicDiscounts(missingIds);
  }
}, [rows]);


  /** Copy coupon code to clipboard (matches HTML copyCouponCode) */
  const copyCouponCode = (code: string, el: HTMLElement | null) => {
    navigator.clipboard.writeText(code).then(() => {
      const originalText = el?.textContent ?? "";
      if (el) {
        el.textContent = "Copied!";
        el.classList.add("coupon-code-copied");
        setTimeout(() => {
          el.textContent = originalText;
          el.classList.remove("coupon-code-copied");
        }, 1500);
      }
    });
  };

  /** Render discount badges - uses SignageOrders.css classes: vendor-coupons, coupon-badge, etc. */
  const renderDiscountBadge = (vendorUserId: string) => {
    const discounts = publicDiscounts[vendorUserId] || [];
    if (discounts.length === 0) return null;

    return (
      <div className="vendor-coupons">
        {discounts.map((discount) => {
          const isPercentage = discount.type === "PERCENTAGE";
          const isAmount = discount.type === "FIXED_AMOUNT";
          const couponValue = isPercentage
            ? `${discount.value}% OFF`
            : isAmount
            ? `$${Number(discount.value) % 1 === 0 ? discount.value : Number(discount.value).toFixed(2)} OFF`
            : `${discount.value} OFF`;
          const couponLabel = isPercentage ? "off" : "off";

          return (
            <div key={discount._id} className="coupon-badge">
              <div className="coupon-left">
                <span className="coupon-icon">🏷️</span>
                <span className="coupon-value">{couponValue}</span>
                <span className="coupon-label">{couponLabel}</span>
              </div>
              <span
                className="coupon-code"
                title="Click to copy"
                role="button"
                tabIndex={0}
                onClick={(e) => copyCouponCode(discount.code, e.currentTarget)}
                onKeyDown={(e) => e.key === "Enter" && copyCouponCode(discount.code, e.currentTarget)}
              >
                {discount.code}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  /** Helpers for Partnership button */
  const getActiveSupplierId = (): string | undefined =>
    activeProfile?.user_id || activeListItem?.user_id || undefined;

  const getActiveVendorName = (): string =>
    activeProfile?.vendor_name || activeListItem?.vendor_name || "Vendor";

  const getActiveVendorEmail = (): string | undefined =>
    activeProfile?.contact_info?.email || undefined;

  const isActiveVendorLinked = (): boolean => {
    const sid = getActiveSupplierId();
    if (!sid) return false;
    return linkedSupplierIds.includes(sid);
  };

  const handleLinkSupplier = async () => {
    if (!API_URL || !token) return;
    const supplierId = getActiveSupplierId();
    const vendorName = getActiveVendorName();
    const vendorEmail = getActiveVendorEmail() || ""; // API expects an email; we send empty if unknown

    if (!supplierId) {
      showError("Missing supplier ID.");
      return;
    }

    const ok = window.confirm(`Do you want to link ${vendorName}?`);
    if (!ok) return;

    try {
      // Determine API endpoint based on user type
      const endpoint = user === "realtor" 
        ? `${API_URL}/office/link-supplier`
        : `${API_URL}/agent/link-supplier`;

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          supplier_id: supplierId,
          email: vendorEmail,
          name: vendorName,
        }),
      });

      const json = await resp.json();
      if (!resp.ok) throw new Error((json as any)?.error || "Failed to link.");

      showSuccess("Supplier linked successfully!");
      // update local linked ids
      setLinkedSupplierIds((prev) => (prev.includes(supplierId) ? prev : [...prev, supplierId]));

      // ✅ Trigger: Supplier Linked - Send notifications
      try {
        const { createNotification } = await import("../../services/notificationApi");
        const { getBrokerageSupplierLinkedContent, getAgentSupplierLinkedContent } = await import("../../services/notificationContent");
        
        // Fetch user info to determine license type and get agent name
        const userRespForNotif = await fetch(`${API_URL}/users/${userId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (userRespForNotif.ok) {
          const userDataForNotif = await userRespForNotif.json();
          const userLicenseType = userDataForNotif.licenseType || (user === "realtor" ? "BROKERAGE_FIRM" : "AGENT");
          const agentName = `${userDataForNotif.firstName || ""} ${userDataForNotif.lastName || ""}`.trim() || userName || "Agent";

          if (user === "realtor") {
            // Trigger 1: Brokerage Supplier Linked
            const recipientType = userLicenseType === "BROKERAGE_FIRM" ? "brokerage_admin" : "brokerage_team_member";
            const companyName = userDataForNotif.firmDetails?.companyName || userDataForNotif.companyName || userName || agentName;
            const linkerName = userLicenseType === "BROKERAGE_TEAM_MEMBER" 
              ? agentName 
              : companyName;
            
            // Notify Brokerage Admin/Team Member
            if (userId && token) {
              const brokerageContent = getBrokerageSupplierLinkedContent({
                supplierName: vendorName,
                recipientType: recipientType,
              });
              await createNotification(userId, brokerageContent, token, recipientType);
            }

            // If the link was initiated by an agent (check if user is actually an agent)
            if (userLicenseType === "BROKERAGE_AGENT" && userId && token) {
              const agentContent = getBrokerageSupplierLinkedContent({
                supplierName: vendorName,
                recipientType: "agent",
              });
              await createNotification(userId, agentContent, token, "agent");
            }

            // Notify Signage Supplier (when linked by brokerage)
            try {
              const supplierResp = await fetch(`${API_URL}/users/${supplierId}`, {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              });

              if (supplierResp.ok) {
                const supplierData = await supplierResp.json();
                const signageCompanyId = supplierData.signage_company_id || supplierData._id;

                // Fetch signage company admin/team members
                const signageTeamResp = await fetch(`${API_URL}/users-search/datatable`, {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    page: 1,
                    limit: 100,
                    filters: {
                      signage_company_id: signageCompanyId,
                      licenseType: ["SIGNAGE_FIRM", "SIGNAGE_TEAM_MEMBER"],
                      status: "Active",
                    },
                    fields: ["id", "email", "licenseType"],
                  }),
                });

                if (signageTeamResp.ok) {
                  const signageTeamData = await signageTeamResp.json();
                  const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

                  // Send notifications to all signage admin/team members
                  for (const member of signageTeamMembers) {
                    const memberId = member.id || member._id;
                    const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                    const supplierRecipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";

                    if (memberId && token) {
                      try {
                        const supplierContent = getBrokerageSupplierLinkedContent({
                          supplierName: vendorName,
                          linkerName: linkerName,
                          recipientType: supplierRecipientType,
                        });
                        await createNotification(memberId, supplierContent, token, supplierRecipientType);
                      } catch (err) {
                        console.warn(`Failed to send notification to signage team member ${memberId}:`, err);
                      }
                    }
                  }
                }
              }
            } catch (signageErr) {
              console.warn("Failed to fetch signage team members for notification:", signageErr);
            }
          } else if (user === "agent") {
            // Trigger 2: Agent Supplier Linked
            // Notify Agent
            if (userId && token) {
              const agentContent = getAgentSupplierLinkedContent({
                supplierName: vendorName,
                agentName: agentName,
                recipientType: "agent",
              });
              await createNotification(userId, agentContent, token, "agent");
            }

            // Notify Signage Admin/Team Members
            // Fetch supplier user data to get signage company ID and notify their team
            try {
              const supplierResp = await fetch(`${API_URL}/users/${supplierId}`, {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              });

              if (supplierResp.ok) {
                const supplierData = await supplierResp.json();
                const signageCompanyId = supplierData.signage_company_id || supplierData._id;

                // Fetch signage company admin/team members
                const signageTeamResp = await fetch(`${API_URL}/users-search/datatable`, {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    page: 1,
                    limit: 100,
                    filters: {
                      signage_company_id: signageCompanyId,
                      licenseType: ["SIGNAGE_FIRM", "SIGNAGE_TEAM_MEMBER"],
                      status: "Active",
                    },
                    fields: ["id", "email", "licenseType"],
                  }),
                });

                if (signageTeamResp.ok) {
                  const signageTeamData = await signageTeamResp.json();
                  const signageTeamMembers = Array.isArray(signageTeamData?.data) ? signageTeamData.data : [];

                  // Send notifications to all signage admin/team members
                  for (const member of signageTeamMembers) {
                    const memberId = member.id || member._id;
                    const memberLicenseType = member.licenseType || "SIGNAGE_TEAM_MEMBER";
                    const recipientType = memberLicenseType === "SIGNAGE_FIRM" ? "signage_admin" : "team_member";

                    if (memberId && token) {
                      try {
                        const signageContent = getAgentSupplierLinkedContent({
                          supplierName: vendorName,
                          agentName: agentName,
                          recipientType: recipientType,
                        });
                        await createNotification(memberId, signageContent, token, recipientType);
                      } catch (err) {
                        console.warn(`Failed to send notification to signage team member ${memberId}:`, err);
                      }
                    }
                  }
                }
              }
            } catch (signageErr) {
              console.warn("Failed to fetch signage team members for notification:", signageErr);
            }
          }
        }
      } catch (notifError) {
        console.warn("Failed to send supplier linked notification:", notifError);
        // Don't block the link operation if notification fails
      }

      // Send email notifications
      try {
        // Fetch user info for email
        const userResp = await fetch(`${API_URL}/users/${userId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (userResp.ok) {
          const userData = await userResp.json();
          const userEmail = userData.email || "";
          const userFirstName = userData.firstName || userName?.split(" ")[0] || "";
          const userLicenseType = userData.licenseType || (user === "realtor" ? "BROKERAGE_FIRM" : "AGENT");
          const companyName = userData.firmDetails?.companyName || userData.companyName || "";

          const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
          const linkedOn = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

          // Determine requester name (Agent Name or Brokerage Firm Name)
          const requesterName = user === "realtor" 
            ? (companyName || userName || userFirstName)
            : (userName || userFirstName);

          // Step 1: Send email to supplier first (SIGNAGE_FIRM/SIGNAGE_TEAM_MEMBER)
          if (vendorEmail) {
            const supplierParams: Record<string, string> = {
              "First Name": userFirstName, // Will be replaced by supplier's first name if available
              "Agent or Brokerage Name": requesterName,
              "Linked On": linkedOn,
              "View Linked Agents/Firms": `${BASE_URL}/signage/suppliers`,
            };

            await sendTriggerEmails(
              "Supplier Linked",
              ["SIGNAGE_FIRM"],
              supplierParams,
              [vendorEmail],
              from,
              token
            );
          }

          // Step 2: Send email to user (AGENT or BROKERAGE_FIRM/BROKERAGE_TEAM_MEMBER)
          const userParams: Record<string, string> = {
            "Agent First Name": userFirstName,
            "First Name": userFirstName,
            "Supplier Name": vendorName,
            "Supplier Company Name": vendorName,
            "Supplier Contact Email": vendorEmail || "N/A",
            "Linked On": linkedOn,
            "Your Name": userName || userFirstName,
            "Type": "Signage",
            "View My Suppliers": `${BASE_URL}${baseRoute}/suppliers`,
            "Manage Suppliers": `${BASE_URL}${baseRoute}/suppliers`,
          };

          // Determine user license type
          let userLicenseTypeForEmail: string;
          if (user === "realtor") {
            userLicenseTypeForEmail = (userLicenseType === "BROKERAGE_TEAM_MEMBER") 
              ? "BROKERAGE_TEAM_MEMBER" 
              : "BROKERAGE_FIRM";
          } else {
            userLicenseTypeForEmail = "AGENT";
          }

          await sendTriggerEmails(
            "Supplier Linked",
            [userLicenseTypeForEmail],
            userParams,
            [userEmail],
            from,
            token
          );
        }
      } catch (emailErr) {
        console.error("Failed to send email notification:", emailErr);
        // Don't fail the link operation if email fails
      }

      navigate(0)
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to link the supplier.");
    }
  };

  const handleUnlinkSupplier = async () => {
    if (!API_URL || !token) return;
    const supplierId = getActiveSupplierId();
    const vendorName = getActiveVendorName();

    if (!supplierId) {
      showError("Missing supplier ID.");
      return;
    }

    const ok = window.confirm(`Do you want to unlink ${vendorName}?`);
    if (!ok) return;

    try {
      // Determine API endpoint based on user type
      const endpoint = user === "realtor"
        ? `${API_URL}/office/suppliers/unlink`
        : `${API_URL}/agent/suppliers/unlink`;

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ supplier_id: supplierId }),
      });

      const json = await resp.json();
      if (!resp.ok) throw new Error((json as any)?.error || "Failed to unlink.");

      showSuccess("Supplier unlinked successfully!");
      // update local linked ids
      setLinkedSupplierIds((prev) => prev.filter((id) => id !== supplierId));

      // Send email notification to supplier
      try {
        const vendorEmail = getActiveVendorEmail();
        if (vendorEmail) {
          // Fetch user info for email
          const userResp = await fetch(`${API_URL}/users/${userId}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (userResp.ok) {
            const userData = await userResp.json();
            const userFirstName = userData.firstName || userName?.split(" ")[0] || "";
            const companyName = userData.firmDetails?.companyName || userData.companyName || "";

            const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
            const requestDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

            // Determine requester name
            const requesterName = user === "realtor" 
              ? (companyName || userName || userFirstName)
              : (userName || userFirstName);

            // Prepare email parameters
            const emailParams: Record<string, string> = {
              "First Name": userFirstName,
              "Agent or Brokerage Name": requesterName,
              "Product Title": vendorName, // Using vendor name as product name for now
              "Request Date": requestDate,
              "Agent note, if any": "", // No reason provided in unlink request
              "Review Unlink Requests": `${BASE_URL}/signage/incoming-products`,
            };

            // Send email to supplier (signage admin/team member)
            await sendTriggerEmails(
              "Supplier Unlink Request",
              ["SIGNAGE_FIRM"],
              emailParams,
              [vendorEmail],
              from,
              token
            );
          }
        }
      } catch (emailErr) {
        console.error("Failed to send email notification:", emailErr);
        // Don't fail the unlink operation if email fails
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to unlink the supplier.");
    }
  };

  /** Handle Products button click - check supplier ID and manage cart */
  const handleProductsClick = async (vendor: VendorListItem) => {
    if (!token || !API_URL) {
      showError("Missing authentication or API URL");
      return;
    }

    const newSupplierId = vendor.user_id;
    const storedSupplierId = localStorage.getItem("supplierId");
    const cartId = localStorage.getItem("cart_id");

    // If supplier IDs match, just navigate
    if (storedSupplierId === newSupplierId) {
      navigate(`${baseRoute}/suppliers/${vendor.user_id}/brand/${vendor._id}`);
      return;
    }

    // If supplier IDs don't match (or no stored supplier ID but cart exists), delete cart and create new one
    if (cartId && (!storedSupplierId || storedSupplierId !== newSupplierId)) {
      try {
        // Delete the entire cart
        await fetch(`${API_URL}/carts/${cartId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        // Create a new cart
        const createCartResponse = await fetch(`${API_URL}/carts`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cart_items: [],
          }),
        });

        if (createCartResponse.ok) {
          const newCartData = await createCartResponse.json();
          if (newCartData.cart_id) {
            localStorage.setItem("cart_id", newCartData.cart_id);
          }
        }
      } catch (err) {
        console.error("Error managing cart:", err);
        showError("Failed to update cart. Please try again.");
        return;
      }
    }

    // Store the new supplier ID and navigate
    localStorage.setItem("supplierId", newSupplierId);
    navigate(`${baseRoute}/suppliers/${vendor.user_id}/brand/${vendor._id}`);
  };

  // Loading / error screens (unchanged style)
  if (loadingList) {
    return (
      <div className="container-xxl py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading vendors...</p>
        </div>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="container-xxl py-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Vendors</h4>
          <p>{listError}</p>
        </div>
      </div>
    );
  }

  const requestSchema = Yup.object({
    vendor_name: Yup.string().trim().required("Vendor name is required"),
    category: Yup.string().required("Category is required"),
    category_other: Yup.string().when("category", {
      is: "other",
      then: (s) => s.trim().required("Please specify the category"),
      otherwise: (s) => s.optional(),
    }),
    website: Yup.string().max(500).nullable().optional(),
    why: Yup.string().max(600, "Keep it under 600 characters").nullable().optional(),
    vendorRelation: Yup.string().oneOf(["", "existing", "multiple"]).optional(),
  });

  const submitVendorRequest = async (values: any, { resetForm }: any) => {
    try {
      if (!API_URL || !token) throw new Error("Missing configuration");
      const vendorRelation = values.vendorRelation || "";
      const category_other = values.category === "other" ? String(values.category_other || "").trim() : undefined;
      const payload = {
        vendor_name: values.vendor_name,
        category: values.category,
        ...(category_other !== undefined && category_other !== "" ? { category_other } : {}),
        website_or_contact: values.website || "",
        recommendation: values.why || "",
        already_work_with: vendorRelation === "existing",
        multiple_offices: vendorRelation === "multiple",
        submitted_by: userName
      };

      const resp = await fetch(`${API_URL}/vendors/requests`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      // don't break functionality if backend not ready
      if (!resp.ok) {
        try {
          const j = await resp.json();
          throw new Error((j as any)?.error || "Failed to submit vendor request");
        } catch (e) {
          throw new Error("Failed to submit vendor request");
        }
      }

      showSuccess("Vendor request submitted. Thank you!");
      resetForm();
      setShowRequestModal(false);
    } catch (e) {
      showError(e instanceof Error ? e.message : "Unable to submit request");
    }
  };
  const openRequestVendorModal = () => setShowRequestModal(true);

  const logoClassByIndex = (i: number) => {
    const classes = ["logo-1", "logo-2", "logo-3", "logo-4", "logo-5", "logo-6", "logo-7", "logo-8", "logo-9"];
    return classes[i % classes.length];
  };

  const renderStars = (avg: number) => {
    const full = Math.floor(avg);
    const half = avg - full >= 0.5 ? 1 : 0;
    const empty = 5 - full - half;
    return (
      <>
        {"★".repeat(full)}
        {half ? "½" : ""}
        {"☆".repeat(empty)}
      </>
    );
  };

  const allVendors = [...featured, ...rest];

  return (
    <main className="main saas-main" id="vendorListPage">
      <section className="hero">
        <h1>Real Estate Professional Services</h1>
        <p>Connect with verified vendors for photography, staging, signage, and everything you need to succeed in real estate.</p>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Don&apos;t see your preferred vendor?</h2>
          <p>Help us prioritize the vendors you want. Request your preferred professionals and we&apos;ll work to bring them into our marketplace.</p>
          <div className="cta-features">
            <div className="cta-feature">
              <div className="check-icon">✓</div>
              <span>Fast vendor evaluation</span>
            </div>
            <div className="cta-feature">
              <div className="check-icon">✓</div>
              <span>All service categories</span>
            </div>
            <div className="cta-feature">
              <div className="check-icon">✓</div>
              <span>Community-driven requests</span>
            </div>
          </div>
        </div>
        <button type="button" className="btn-primary1" onClick={openRequestVendorModal}>Submit Request</button>
      </section>

      <div className="controls">
        <div className="search-wrapper">
          <span className="search-icon" aria-hidden>🔍</span>
          <input
            type="text"
            placeholder="Search vendors, services, skills..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPage(1);
                setSearchQuery(searchInput);
              }
            }}
          />
        </div>
        <div className="select-wrapper">
          <select className="select1" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="All">All Categories</option>
            {categories?.filter((c) => c !== "All").map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <span className="select1-arrow" aria-hidden>▼</span>
        </div>
        <button
          type="button"
          className={`btn-secondary ${onlyPartners ? "active" : ""}`}
          onClick={() => setOnlyPartners((v) => !v)}
        >
          ★ My Favorites
        </button>
      </div>

      <div className="vendors-header">
        <div className="vendors-count">Showing {filteredRows.length} verified vendors</div>
      </div>

      <div className="vendors-grid" id="vendorsList">
        {allVendors.map((v, idx) => {
          const ratingData = profileRatings[v._id];
          const avgRating = ratingData?.avg ?? 0;
          const reviewCount = ratingData?.count ?? 0;
          const badgeClass = v.verified ? "verified" : v.partner ? "partner" : "new";
          const badgeText = v.verified ? "Verified" : v.partner ? "Partner" : "New";

          return (
            <div key={v._id} className="vendor-card box-shadow-datatables shadow-border-2">
              <div className="vendor-image">
                <div className={`vendor-logo ${!v.logo ? logoClassByIndex(idx) : ""}`}>
                  {v.logo ? (
                    <img src={getImageSrc(v.logo, logoUrlMap)} alt={v.vendor_name} style={{ width: 167, height: 86 }} />
                  ) : (
                    v.vendor_name?.charAt(0)?.toUpperCase() || "V"
                  )}
                </div>
              </div>
              {(v.verified || v.partner) && (
                <div className={`vendor-badge ${badgeClass}`}>{badgeText}</div>
              )}
              <div className="vendor-body">
                <div className="vendor-title">{v.vendor_name}</div>
                <div className="vendor-subtitle">{v.tag_line || ""}</div>
                <div className="vendor-info">
                  <div className="info-row">
                    <span className="info-label">Service</span>
                    <span className="info-value">{v.Service || v.category || "—"}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Coverage</span>
                    <span className="info-value">—</span>
                  </div>
                  <div className="rating">
                    <span className="stars">{avgRating > 0 ? renderStars(avgRating) : "—"}</span>
                    <span>({reviewCount})</span>
                  </div>
                </div>
                {renderDiscountBadge(v.user_id)}
                {(v.core_specialties?.length ?? 0) > 0 && (
                  <div className="vendor-tags">
                    {v.core_specialties!.map((tag) => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                )}
                {(!v.core_specialties?.length) && v.category && (
                  <div className="vendor-tags">
                    <span className="tag">{v.category}</span>
                  </div>
                )}
                <div className="vendor-footer">
                  <button type="button" className="btn-outline" onClick={() => openDetails(v)}>Details</button>
                  <button type="button" className="btn-action" onClick={() => handleProductsClick(v)}>View Catalog</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {allVendors.length === 0 && (
        <div className="vendors-grid">
          <div className="vendor-card" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "2rem" }}>
            No vendors match your filters.
          </div>
        </div>
      )}

      <VendorDetailsSlider
        show={showDetails}
        onClose={closeDetails}
        profile={activeProfile}
        listItem={activeListItem}
        logoUrlMap={logoUrlMap || {}}
        loading={loadingDetails}
        detailsError={detailsError}
        avgRating={activeListItem ? profileRatings[activeListItem._id]?.avg : undefined}
        reviewCount={activeListItem ? profileRatings[activeListItem._id]?.count : undefined}
        reviews={reviews}
        loadingReviews={loadingReviews}
        reviewsError={reviewsError}
        discounts={(() => {
          const v = activeProfile || activeListItem;
          return (v?.user_id && publicDiscounts[v.user_id]) || [];
        })()}
        onCopyCouponCode={copyCouponCode}
        onConnect={() => { const sid = getActiveSupplierId(); if (sid) localStorage.setItem("supplierId", sid); setShowConnectModal(true); }}
        onWriteReview={openReview}
        isLinked={isActiveVendorLinked()}
        loadingLinked={loadingLinked}
      />

      {/* REVIEW MODAL (refreshes reviews on submit) */}
      <ReviewModal
        show={showReview}
        vendor={activeProfile ? { name: activeProfile?.vendor_name, id: activeProfile?._id, logo: activeProfile?.logo } : null}
        onClose={closeReview}
        stacked={showDetails}
        reviewType="vendor"
        profileId={activeProfile?._id}
        logoUrlMap={logoUrlMap || {}}
        onSubmit={async () => {
          if (activeProfile?._id) await loadVendorReviews(activeProfile._id);
        }}
      />

      {/* CONNECT VENDOR MODAL – request connection (POST /Signage/requestsent) */}
      <ConnectVendorModal
        show={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        vendor={activeProfile || activeListItem ? {
          id: getActiveSupplierId() || activeProfile?._id || activeListItem?._id || "",
          name: getActiveVendorName(),
          logo: activeProfile?.logo ? getImageSrc(activeProfile.logo, logoUrlMap) : undefined,
        } : null}
        userType={user}
        onSuccess={() => {
          const sid = getActiveSupplierId();
          if (sid) setLinkedSupplierIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
        }}
      />

      {/* REQUEST VENDOR MODAL - uses connect-modal / request-vendor classnames from provided HTML */}
      {showRequestModal && (
        <>
          <div
            className="modal-overlay active"
            id="requestVendorModalOverlay"
            onClick={() => setShowRequestModal(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Escape" && setShowRequestModal(false)}
            aria-label="Close modal"
          />
          <div
            className="connect-modal active"
            id="requestVendorModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="requestVendorModalTitle"
          >
            <div className="connect-modal-header">
              <div className="connect-modal-title-section">
                <h2 className="connect-modal-title" id="requestVendorModalTitle">Help Us Prioritize Vendors You Want</h2>
                <p className="request-vendor-subtitle">Request your favorite signage, staging, or service vendor and we&apos;ll work to bring them into RealEsy.</p>
              </div>
              <button type="button" className="modal-close-btn" onClick={() => setShowRequestModal(false)} aria-label="Close">×</button>
            </div>
            <div className="connect-modal-content" id="requestVendorModalContent">
              <Formik
                initialValues={{
                  vendor_name: "",
                  category: "",
                  category_other: "",
                  website: "",
                  why: "",
                  vendorRelation: "",
                  submitted_by: "",
                }}
                validationSchema={requestSchema}
                onSubmit={submitVendorRequest}
              >
                {({ isSubmitting, values }) => (
                  <Form id="requestVendorForm">
                    {/* Vendor Name */}
                    <div className="request-form-group">
                      <label className="request-form-label" htmlFor="vendor_name">
                        <span className="request-label-icon">📋</span>
                        Vendor Name <span className="required">*</span>
                      </label>
                      <div className="request-input-wrapper">
                        <span className="request-input-icon">👤</span>
                        <Field
                          name="vendor_name"
                          id="vendor_name"
                          type="text"
                          className="request-form-input"
                          placeholder="Enter vendor or company name"
                        />
                      </div>
                      <div className="invalid-feedback d-block"><ErrorMessage name="vendor_name" /></div>
                    </div>

                    {/* Category/Type of Vendor */}
                    <div className="request-form-group">
                      <label className="request-form-label" htmlFor="category">
                        <span className="request-label-icon">📊</span>
                        Category/Type of Vendor <span className="required">*</span>
                      </label>
                      <div className="request-input-wrapper">
                        <span className="request-input-icon">📦</span>
                        <Field as="select" name="category" id="category" className="request-form-select">
                          <option value="">Select vendor category</option>
                          <option value="photography">Photography</option>
                          <option value="staging">Staging</option>
                          <option value="signage">Signage</option>
                          <option value="virtual-tours">Virtual Tours</option>
                          <option value="cleaning">Cleaning</option>
                          <option value="landscaping">Landscaping</option>
                          <option value="repairs">Repairs &amp; Maintenance</option>
                          <option value="design">Design &amp; Marketing</option>
                          <option value="logistics">Logistics &amp; Access</option>
                          <option value="other">Other</option>
                        </Field>
                      </div>
                      <div className="invalid-feedback d-block"><ErrorMessage name="category" /></div>
                      {values.category === "other" && (
                        <div className="request-form-group" style={{ marginTop: 12 }}>
                          <label className="request-form-label" htmlFor="category_other">
                            <span className="request-label-icon">✏️</span>
                            Specify category <span className="required">*</span>
                          </label>
                          <div className="request-input-wrapper">
                            <span className="request-input-icon">📦</span>
                            <Field
                              name="category_other"
                              id="category_other"
                              type="text"
                              className="request-form-input"
                              placeholder="Enter category or type of vendor"
                            />
                          </div>
                          <div className="invalid-feedback d-block"><ErrorMessage name="category_other" /></div>
                        </div>
                      )}
                    </div>

                    {/* Website or Contact Info */}
                    <div className="request-form-group">
                      <label className="request-form-label" htmlFor="website">
                        <span className="request-label-icon">🔗</span>
                        Website or Contact Info <span className="optional">(optional but useful)</span>
                      </label>
                      <div className="request-input-wrapper">
                        <span className="request-input-icon">🌐</span>
                        <Field
                          name="website"
                          id="website"
                          type="text"
                          className="request-form-input"
                          placeholder="Website URL, phone number, or email"
                        />
                      </div>
                      <div className="invalid-feedback d-block"><ErrorMessage name="website" /></div>
                    </div>

                    {/* Why You Recommend Them */}
                    <div className="request-form-group">
                      <label className="request-form-label" htmlFor="why">
                        <span className="request-label-icon">💭</span>
                        Why You Recommend Them
                      </label>
                      <div className="request-textarea-wrapper">
                        <Field
                          as="textarea"
                          name="why"
                          id="why"
                          className="request-form-textarea"
                          placeholder="Tell us what makes this vendor great (quality, service, pricing, etc.)"
                          rows={4}
                        />
                        <span className="textarea-icon">💬</span>
                      </div>
                      <div className="invalid-feedback d-block"><ErrorMessage name="why" /></div>
                    </div>

                    {/* Radio Options */}
                    <div className="request-radio-group" role="group" aria-label="Vendor relation">
                      <label className="request-radio-item">
                        <Field type="radio" name="vendorRelation" value="existing" />
                        <div className="radio-content">
                          <span className="radio-title">I already work with this vendor</span>
                          <span className="radio-description">Helps us prioritize vendors with active users</span>
                        </div>
                      </label>
                      <label className="request-radio-item">
                        <Field type="radio" name="vendorRelation" value="multiple" />
                        <div className="radio-content">
                          <span className="radio-title">This vendor serves multiple offices in my brokerage</span>
                          <span className="radio-description">Highlights scalable vendor options</span>
                        </div>
                      </label>
                    </div>

                    {/* Submit Buttons - Cancel left, Submit right */}
                    <div className="request-form-actions" style={{ justifyContent: "space-between" }}>
                      <button type="button" className="btn-request-cancel" onClick={() => setShowRequestModal(false)}>Cancel</button>
                      <button type="submit" className="btn-request-submit" disabled={isSubmitting}>Submit Vendor Request</button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </>
      )}


      {/* Local styles (unchanged except button colors already exist) */}

    </main>
  );
};

export default VendorsPage;
